import { useMemo } from 'react';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { useClinicTheme } from '@/hooks/useClinicTheme';
import type { RawThemeInput } from '@/types/clinic';

export function ThemeInitializer() {
  const { config } = useClinicConfigContext();

  const rawTheme = useMemo(() => {
    // Check for form_structure.theme.theme.color_scheme (new structure)
    const formStructureTheme = config?.form_structure?.theme?.theme?.color_scheme;
    
    // Support both top-level and nested `theme.color_scheme` (existing structures)
    const topLevel = config?.active_theme?.color_scheme;
    const nested = config?.active_theme?.theme?.color_scheme;
    
    // Use the first available theme
    const cs = (formStructureTheme || nested || topLevel) as
      | {
          primary: string;
          secondary: string;
          main?: string;
          gradient: string | null;
          'icon-stroke'?: string;
          'text-color'?: string;
          'second-text-color'?: string;
        }
      | undefined;
    if (cs) {
      const gradient = cs.gradient === 'null' ? null : (cs.gradient ?? null);

      const dict = cs as unknown as Record<string, unknown>;
      const getString = (
        obj: Record<string, unknown>,
        keys: string[],
        fallback: string
      ): string => {
        for (const key of keys) {
          const value = obj[key];
          if (typeof value === 'string' && value.trim() !== '') return value;
        }
        return fallback;
      };

      const textColor = getString(
        dict,
        ['text-color', 'text_color', 'textColor'],
        '#000'
      );
      const secondTextColor = getString(
        dict,
        ['second-text-color', 'second_text_color', 'secondTextColor'],
        cs.primary
      );
      const iconStroke = getString(
        dict,
        ['icon-stroke', 'icon_stroke', 'iconStroke'],
        cs.secondary
      );

      return {
        primary: cs.primary,
        secondary: cs.secondary,
        main: cs.main ?? cs.primary,
        gradient,
        'icon-stroke': iconStroke,
        'text-color': textColor,
        'second-text-color': secondTextColor,
      };
    }

    // Hydrate from localStorage to prevent theme reset on back/refresh
    try {
      const stored = localStorage.getItem('clinic_config');
      if (stored) {
        const parsed = JSON.parse(stored) as { active_theme?: any };
        const fallback =
          parsed?.active_theme?.theme?.color_scheme ||
          parsed?.active_theme?.color_scheme;
        if (fallback) {
          const dict = fallback as Record<string, string>;
          const get = (k: string, fb: string) =>
            typeof dict[k] === 'string' && dict[k].trim() !== '' ? dict[k] : fb;
          const gradient = get('gradient', '') || null;
          return {
            primary: get('primary', '#2563eb'),
            secondary: get('secondary', '#1d4ed8'),
            main: get('main', get('primary', '#2563eb')),
            gradient,
            'icon-stroke': get('icon-stroke', get('secondary', '#1d4ed8')),
            'text-color': get('text-color', '#1f2937'),
            'second-text-color': get(
              'second-text-color',
              get('primary', '#2563eb')
            ),
          } as RawThemeInput;
        }
      }
    } catch (_) {
      // ignore
    }

    // Return null to avoid applying an incorrect default
    return null;
  }, [config]);

  // Always call hooks unconditionally; the hook itself guards on null/undefined
  useClinicTheme(rawTheme as RawThemeInput | null);
  return null;
}

export default ThemeInitializer;
